import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import type { OverallDescriptionDocType } from '@ast-data/shared';
import { useFamilyDocs } from '../../db/useScopedDocs';
import type { FindingRefRange } from '../../components/refEditor/findingRefRanges';
import type { FindingRefSupport } from '../../components/RichTextArea';
import { useFamilyDescriptions } from './useFamilyDescriptions';
import { useFindingBasePath } from './findingRefLabels';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';

/**
 * Turns the Befund and Gesamtbeschreibung references a text names into links.
 *
 * The parser only reads what the prose SAYS ("Bef. 7000 Fl. 1 Pl. 2"); this is
 * what decides whether that names anything real and where it goes. A reference
 * that resolves becomes a link; one that does not stays marked but dead, which
 * is how a mistyped number in a Tagebuch shows itself instead of hiding.
 *
 * Resolved against the whole Teilmaßnahmen-FAMILY, not the one project: the
 * Befunde are shared across its phases, and a day's text names them by number
 * without knowing which phase recorded them.
 *
 * `onNavigate` is how a chip inside an EDITOR keeps its unsaved changes: pass
 * `useGuardedNavigate`'s `requestNavigate` and a click on a reference asks
 * before leaving, exactly as the Verweise chips already do. Omitted — outside
 * an editor, where there is nothing to lose — it navigates straight away.
 */
export function useFindingRefSupport(
    projectId: string,
    onNavigate?: (to: string) => void
): FindingRefSupport {
    const { findings, planums, profiles } = useFamilyDescriptions(projectId);
    const { results: overallDescriptions } =
        useFamilyDocs<OverallDescriptionDocType>(
            'overall_descriptions',
            useFamilyProjectIds(projectId)
        );
    const findingBase = useFindingBasePath();
    const inField = useLocation().pathname.startsWith('/field');
    const navigate = useNavigate();
    const intl = useIntl();

    /**
     * Reference -> route, or '' when nothing matches.
     *
     * A Map keyed by what the text can say, built once per data change: this is
     * asked once per reference on every redraw, so it must be a lookup and not
     * a search. The key carries the description part, so "Bef. 7000" and
     * "Bef. 7000 Pl. 2" resolve to different records — the whole Befund and one
     * of its Planum sheets.
     */
    const routeByKey = useMemo(() => {
        const key = (
            number: string,
            part: 'planum' | 'profile' | 'overall' | '' = '',
            value = ''
        ) => `${number.trim().toLowerCase()}|${part}|${value.trim()}`;

        const byId = new Map(findings.map((finding) => [finding.id, finding]));
        const routes = new Map<string, string>();
        for (const finding of findings) {
            if (finding.findingNumber) {
                routes.set(
                    key(finding.findingNumber),
                    `${findingBase(finding.projectId, finding.id)}/edit`
                );
            }
        }
        for (const planum of planums) {
            const finding = byId.get(planum.findingId);
            if (!finding?.findingNumber || !planum.planumNumber) {
                continue;
            }
            routes.set(
                key(finding.findingNumber, 'planum', planum.planumNumber),
                `${findingBase(planum.projectId, planum.findingId)}/planums/${planum.id}/edit`
            );
        }
        for (const profile of profiles) {
            const finding = byId.get(profile.findingId);
            if (!finding?.findingNumber || !profile.profileNumber) {
                continue;
            }
            routes.set(
                key(finding.findingNumber, 'profile', profile.profileNumber),
                `${findingBase(profile.projectId, profile.findingId)}/profiles/${profile.id}/edit`
            );
        }
        for (const overallDescription of overallDescriptions) {
            const number = overallDescription.overallDescriptionNumber;
            if (!number) {
                continue;
            }
            const base = inField
                ? `/field/${overallDescription.projectId}`
                : `/office/projects/${overallDescription.projectId}`;
            routes.set(
                key(number, 'overall'),
                `${base}/overall-descriptions/${overallDescription.id}/edit`
            );
        }
        return routes;
    }, [
        findings,
        planums,
        profiles,
        overallDescriptions,
        findingBase,
        inField
    ]);

    // The Fläche is deliberately NOT part of the key. It qualifies the Planum in
    // the sentence ("Bef. 7000 Fl. 1 Pl. 2") but the Planumnummer already
    // identifies the sheet within its Befund, and a recorder who leaves the
    // Fläche out — or writes a different one — still means that sheet.
    const routeFor = useCallback(
        ({ ref }: FindingRefRange): string => {
            const number = ref.number.trim().toLowerCase();
            if (ref.overallDescription) {
                return routeByKey.get(`${number}|overall|`) ?? '';
            }
            if (ref.planumNumber) {
                return (
                    routeByKey.get(`${number}|planum|${ref.planumNumber}`) ?? ''
                );
            }
            if (ref.profileNumber) {
                return (
                    routeByKey.get(`${number}|profile|${ref.profileNumber}`) ??
                    ''
                );
            }
            return routeByKey.get(`${number}||`) ?? '';
        },
        [routeByKey]
    );

    const resolves = useCallback(
        (range: FindingRefRange) => routeFor(range) !== '',
        [routeFor]
    );

    const onOpen = useCallback(
        (range: FindingRefRange) => {
            const to = routeFor(range);
            if (!to) {
                return;
            }
            if (onNavigate) {
                onNavigate(to);
            } else {
                navigate(to);
            }
        },
        [routeFor, navigate, onNavigate]
    );

    /**
     * The tooltip, and the only place Ctrl+click is spelled out.
     *
     * Named after the modifier the reader actually has: a Mac says Cmd, and a
     * hint that names the wrong key is worse than none. Detected off the
     * platform string rather than a setting — this is about the keyboard in
     * front of the person, not about the app.
     */
    const titleFor = useCallback(
        (range: FindingRefRange) =>
            intl.formatMessage(
                { id: 'findingRef.openHint' },
                {
                    key: /mac/i.test(navigator.platform) ? '⌘' : 'Strg',
                    label: range.text
                }
            ),
        [intl]
    );

    return useMemo(
        () => ({ resolves, onOpen, titleFor }),
        [resolves, onOpen, titleFor]
    );
}
